import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  addTileFromQuestion, addWidget, askCanvas, askWidget, createDashboard, getDashboard, getDashboards, prepareWidget,
  refreshWidget, removeWidget, renderWidget, restoreWidgetVersion, suggestTiles, updateDashboard, writeCanvasSummary,
} from '../../../services/ai-explorer/dashboardService';
import {
  arrange as arrangeTiles, firstFit, forSave, insightWidget, isLive, pinnedAnswer, placeTile, readActiveCanvas,
  tileSize, widgetFromMessage, withLayouts, writeActiveCanvas,
} from './canvasModel';

const CHANGED_EVENT = 'saf3ai:canvas-changed';
const SAVE_DELAY_MS = 600;
const UNDO_MS = 6000;
// A canvas started by a pin is named after the question, kept short.
const defaultTitle = (question) => String(question || '').replace(/\s+/g, ' ').trim().slice(0, 60);
const detail = (err) => err?.response?.data?.detail || err?.response?.data?.error || '';

/**
 * The canvas the agent pins into: one AI dashboard, remembered as the active
 * canvas so every chat (and the slide-in panel on any page) keeps adding to
 * it until another is picked. Also drives the canvas page itself, where a
 * canvas someone shared with the org opens read-only (`canvas.role === 'viewer'`).
 *
 * Tiles are *live* (redrawn by the server with no model call) or *saved
 * answers* (the agent's answer as given). `views` holds redraws that are not
 * saved: a canvas filter, or a viewer's own copy of a shared canvas.
 *
 * `ensureSaved()` saves the conversation and returns its id, so a tile can
 * always point back at the chat and message it came from.
 */
export default function useCanvas({ t, toast, ensureSaved, enabled = true, remember = true }) {
  const [canvas, setCanvas] = useState(null);
  const [status, setStatus] = useState('idle');          // idle | loading | error
  const [list, setList] = useState({ items: [], loading: false, error: false });
  const [busy, setBusy] = useState({});                  // widget id → 'refresh' | 'ask' | 'prepare' | 'render'
  const [views, setViews] = useState({});                // widget id → redraw not saved (filter / viewer)
  const [canvasBusy, setCanvasBusy] = useState(null);    // 'summary' | 'refreshAll' | null
  const canvasRef = useRef(canvas); canvasRef.current = canvas;
  const saveTimer = useRef(null);
  const instance = useRef(Math.random().toString(36).slice(2));

  const mark = useCallback((id, what) => setBusy((b) => {
    const next = { ...b };
    if (what) next[id] = what; else delete next[id];
    return next;
  }), []);
  const patchWidget = useCallback((canvasId, widgetId, patch) => setCanvas((c) => (c && c.id === canvasId ? {
    ...c, widgets: c.widgets.map((w) => (w.id === widgetId ? { ...w, ...(typeof patch === 'function' ? patch(w) : patch) } : w)),
  } : c)), []);

  const announce = useCallback((id) => {
    window.dispatchEvent(new CustomEvent(CHANGED_EVENT, { detail: { id, from: instance.current } }));
  }, []);

  /** Redraw live tiles without saving: for a filter, or for a viewer of a shared canvas. */
  const renderLive = useCallback(async (c, { focus } = {}) => {
    const targets = (c?.widgets || []).filter((w) => isLive(w) || w.needs_render);
    if (!targets.length) return;
    await Promise.all(targets.map(async (w) => {
      mark(w.id, 'render');
      try {
        const res = await renderWidget(c.id, w.id, { timeRange: c.time_range, focus });
        setViews((v) => ({ ...v, [w.id]: { ...res.data, error: false } }));
      } catch {
        setViews((v) => ({ ...v, [w.id]: { error: true } }));
      } finally { mark(w.id, null); }
    }));
  }, [mark]);

  const load = useCallback(async (id, { quiet = false } = {}) => {
    if (!id) return null;
    if (!quiet) setStatus('loading');
    try {
      const res = await getDashboard(id);
      const doc = res.data || {};
      const next = { ...doc, id: doc.id || id, widgets: withLayouts(doc.widgets) };
      setCanvas(next);
      setViews({});
      setStatus('idle');
      // Someone else's shared canvas is never where your pins go.
      if (remember && next.role !== 'viewer') writeActiveCanvas(next.id);
      if (next.role === 'viewer' || next.focus) renderLive(next, { focus: next.focus });
      return next;
    } catch (err) {
      if (err?.response?.status === 404 && readActiveCanvas() === id) writeActiveCanvas(null);
      if (canvasRef.current?.id === id) setCanvas(null);
      setStatus(quiet ? 'idle' : 'error');
      return null;
    }
  }, [remember, renderLive]);

  // Reopen the last active canvas once, quietly: a deleted one just clears.
  useEffect(() => {
    if (!enabled || !remember || canvasRef.current) return;
    const id = readActiveCanvas();
    if (id) load(id, { quiet: true });
  }, [enabled, load, remember]);

  // The page and the panel each hold a copy; reload when the other changes it.
  useEffect(() => {
    const onChange = (e) => {
      const { id, from } = e.detail || {};
      if (from !== instance.current && id && id === canvasRef.current?.id) load(id, { quiet: true });
    };
    window.addEventListener(CHANGED_EVENT, onChange);
    return () => window.removeEventListener(CHANGED_EVENT, onChange);
  }, [load]);

  useEffect(() => () => clearTimeout(saveTimer.current), []);

  const loadList = useCallback(() => {
    setList((l) => ({ ...l, loading: true, error: false }));
    getDashboards()
      .then((res) => setList({ items: res.data?.items || [], loading: false, error: false }))
      .catch(() => setList({ items: [], loading: false, error: true }));
  }, []);

  /** The canvas started from this conversation, if there is one. */
  const findForConversation = useCallback(async (conversationId) => {
    if (!conversationId) return null;
    try {
      const res = await getDashboards({ conversation_id: conversationId, per_page: 1 });
      return res.data?.items?.[0]?.id || null;
    } catch { return null; }
  }, []);

  const create = useCallback(async ({ title, conversationId } = {}) => {
    const clean = String(title || '').trim().slice(0, 200) || t('agent.canvasUntitled');
    const res = await createDashboard({ title: clean, conversation_id: conversationId || undefined });
    const next = { id: res.data?.id, title: res.data?.title || clean, widgets: [], conversation_id: conversationId || null, role: 'owner' };
    if (!next.id) throw new Error('no canvas id');
    setCanvas(next);
    setViews({});
    setStatus('idle');
    writeActiveCanvas(next.id);
    return next;
  }, [t]);

  /** Start an empty canvas now (from the canvas menu). */
  const startNew = useCallback(async (title) => {
    try {
      const conversationId = await ensureSaved?.();
      return await create({ title, conversationId });
    } catch { toast(t('agent.canvasSaveFailed'), 'error'); return null; }
  }, [create, ensureSaved, t, toast]);

  /** Resolve a pinned answer's question and decide live vs saved answer, in the background. */
  const prepare = useCallback(async (canvasId, widget, { context, pageContext, toolCalls } = {}) => {
    if (!widget?.id || widget.kind === 'insight') return;
    mark(widget.id, 'prepare');
    try {
      const res = await prepareWidget(canvasId, widget.id, {
        context: context || [], page_context: pageContext || undefined, tool_calls: toolCalls || [],
      });
      const { mode, dimension, question } = res.data || {};
      patchWidget(canvasId, widget.id, (w) => ({
        query: question || w.query,
        spec: { ...(w.spec || {}), mode, dimension, question: question || w.query },
      }));
    } catch { /* the tile simply stays a saved answer */ }
    finally { mark(widget.id, null); }
  }, [mark, patchWidget]);

  const append = useCallback(async (body, { canvasTitle, context, pageContext, toolCalls, quiet } = {}) => {
    const conversationId = (await ensureSaved?.()) || null;
    let target = canvasRef.current;
    if (!target || target.role === 'viewer') target = await create({ title: canvasTitle, conversationId });
    const withSource = { ...body, conversation_id: body.conversation_id || conversationId };
    const layout = firstFit((target.widgets || []).map((w) => w.layout), tileSize(withSource));
    const res = await addWidget(target.id, { ...withSource, layout });
    const widget = res.data?.widget || { ...withSource, layout, id: res.data?.widget_id };
    setCanvas((c) => (c && c.id === target.id ? { ...c, widgets: [...c.widgets, widget] } : { ...target, widgets: [...(target.widgets || []), widget] }));
    announce(target.id);
    if (!quiet) toast(t('agent.pinnedTo', { title: target.title || t('agent.canvasUntitled') }));
    prepare(target.id, widget, { context, pageContext, toolCalls });
    return target.id;
  }, [announce, create, ensureSaved, prepare, t, toast]);

  /** Pin one answer. Returns the canvas id, or null when it failed. */
  const pin = useCallback(async (m, { question, context, pageContext, quiet } = {}) => {
    try {
      // The answer's tool calls let the server redraw it later by replaying them.
      // Only the newest read calls matter (the server replays at most a few).
      const toolCalls = (m.steps || []).filter((st) => st?.name && !st.write).slice(-20)
        .map((st) => ({ name: st.name, arguments: st.arguments || {} }));
      return await append(widgetFromMessage(m, { question, untitled: t('agent.untitled') }),
        { canvasTitle: defaultTitle(question), context, pageContext, toolCalls, quiet });
    } catch { toast(t('agent.canvasPinFailed'), 'error'); return null; }
  }, [append, t, toast]);

  /** Pin a highlighted sentence as an insight. */
  const pinText = useCallback(async (text, { messageId, question } = {}) => {
    try {
      return await append(insightWidget(text, { question, messageId }), { canvasTitle: defaultTitle(question) });
    } catch { toast(t('agent.canvasPinFailed'), 'error'); return null; }
  }, [append, t, toast]);

  // Removing a tile hides it at once and deletes it after a few seconds, so
  // the toast's Undo can bring it back untouched (spec, versions and all).
  const pendingRemovals = useRef(new Map());
  const commitRemoval = useCallback(async (canvasId, widget) => {
    pendingRemovals.current.delete(widget.id);
    try {
      await removeWidget(canvasId, widget.id);
      announce(canvasId);
    } catch {
      setCanvas((cur) => (cur && cur.id === canvasId && !cur.widgets.some((w) => w.id === widget.id)
        ? { ...cur, widgets: [...cur.widgets, widget] } : cur));
      toast(t('agent.canvasSaveFailed'), 'error');
    }
  }, [announce, t, toast]);
  const unpin = useCallback(async (widgetId) => {
    const c = canvasRef.current;
    const widget = c?.widgets.find((w) => w.id === widgetId);
    if (!c || !widget) return;
    setCanvas({ ...c, widgets: c.widgets.filter((w) => w.id !== widgetId) });
    const timer = setTimeout(() => commitRemoval(c.id, widget), UNDO_MS);
    pendingRemovals.current.set(widgetId, { timer, canvasId: c.id, widget });
    toast(t('agent.unpinned'), 'ok', {
      label: t('agent.undo'),
      onClick: () => {
        const pending = pendingRemovals.current.get(widgetId);
        if (!pending) return;
        clearTimeout(pending.timer);
        pendingRemovals.current.delete(widgetId);
        setCanvas((cur) => (cur && cur.id === pending.canvasId ? { ...cur, widgets: [...cur.widgets, pending.widget] } : cur));
      },
    });
  }, [commitRemoval, t, toast]);
  // Leaving the page finishes removals that were still waiting for Undo.
  useEffect(() => () => {
    pendingRemovals.current.forEach(({ timer, canvasId, widget }) => { clearTimeout(timer); removeWidget(canvasId, widget.id).catch(() => {}); });
  }, []);

  const saveLayout = useCallback((c) => {
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      updateDashboard(c.id, { widgets: c.widgets.map(forSave) })
        .then(() => announce(c.id))
        .catch(() => toast(t('agent.canvasSaveFailed'), 'error'));
    }, SAVE_DELAY_MS);
  }, [announce, t, toast]);

  /** Commit a move or resize: pushes neighbours aside, then saves. */
  const place = useCallback((widgetId, rect) => {
    const c = canvasRef.current;
    if (!c || c.role === 'viewer') return;
    const next = { ...c, widgets: placeTile(c.widgets, widgetId, rect) };
    setCanvas(next);
    saveLayout(next);
  }, [saveLayout]);

  /** Change fields on one tile (title, note text, Show as, its own time window) and save. */
  const saveTile = useCallback((widgetId, patch) => {
    const c = canvasRef.current;
    if (!c || c.role === 'viewer') return;
    const next = { ...c, widgets: c.widgets.map((w) => (w.id === widgetId ? { ...w, ...patch } : w)) };
    setCanvas(next);
    saveLayout(next);
  }, [saveLayout]);

  /** A note, a section heading, or an insight (an answer about the canvas) written on it. */
  const addText = useCallback(async (kind, { text = '', color, query } = {}) => {
    const c = canvasRef.current;
    if (!c || c.role === 'viewer') return null;
    const title = { section: text || t('agent.sectionUntitled'), insight: text.slice(0, 80) }[kind] || t('agent.note');
    const body = {
      title, query: query || kind, display_type: 'text', kind, data: { text }, ...(color ? { color } : {}),
    };
    try {
      const layout = firstFit(c.widgets.map((w) => w.layout), tileSize(body));
      const res = await addWidget(c.id, { ...body, layout });
      const widget = res.data?.widget || { ...body, layout, id: res.data?.widget_id };
      setCanvas((cur) => (cur && cur.id === c.id ? { ...cur, widgets: [...cur.widgets, widget] } : cur));
      announce(c.id);
      return widget;
    } catch { toast(t('agent.canvasSaveFailed'), 'error'); return null; }
  }, [announce, t, toast]);

  /** Ask a question on the canvas; the answer becomes a new tile (2 model calls). */
  const addTileFrom = useCallback(async (question) => {
    const c = canvasRef.current;
    if (!c || c.role === 'viewer') return false;
    setCanvasBusy('addTile');
    try {
      const res = await addTileFromQuestion(c.id, question);
      const w = res.data?.widget;
      if (w) setCanvas((cur) => (cur && cur.id === c.id ? { ...cur, widgets: [...cur.widgets, w] } : cur));
      announce(c.id);
      return true;
    } catch (err) { toast(detail(err) || t('agent.addTileFailed'), 'error'); return false; }
    finally { setCanvasBusy(null); }
  }, [announce, t, toast]);

  /** Answer a question from what the canvas shows (1 model call, nothing saved). */
  const askAbout = useCallback(async (question) => {
    const c = canvasRef.current;
    if (!c) return null;
    setCanvasBusy('ask');
    try { return (await askCanvas(c.id, question)).data || null; } catch (err) {
      toast(detail(err) || t('agent.askCanvasFailed'), 'error'); return null;
    } finally { setCanvasBusy(null); }
  }, [t, toast]);

  const suggest = useCallback(async () => {
    const c = canvasRef.current;
    if (!c) return [];
    setCanvasBusy('suggest');
    try { return (await suggestTiles(c.id)).data?.questions || []; } catch (err) {
      toast(detail(err) || t('agent.suggestFailed'), 'error'); return [];
    } finally { setCanvasBusy(null); }
  }, [t, toast]);

  /** Lay the whole canvas out as a brief, a grid or a story (no model call). */
  const arrange = useCallback((style) => {
    const c = canvasRef.current;
    if (!c || c.role === 'viewer' || !c.widgets.length) return;
    const next = { ...c, widgets: arrangeTiles(c.widgets, style) };
    setCanvas(next);
    saveLayout(next);
  }, [saveLayout]);

  /** Pin several answers in order, then lay the canvas out in one style (no model call for the layout). */
  const pinMany = useCallback(async (items, style) => {
    let id = null;
    for (const item of items) {
      // Sequential on purpose: each pin may create the canvas the next one lands on.
      // eslint-disable-next-line no-await-in-loop
      id = (await pin(item.m, { ...item, quiet: true })) || id;
    }
    if (id && style) arrange(style);
    if (id) toast(t('agent.pinnedTo', { title: canvasRef.current?.title || t('agent.canvasUntitled') }));
    return id;
  }, [arrange, pin, t, toast]);

  const saveSettings = useCallback(async (patch, local) => {
    const c = canvasRef.current;
    if (!c) return null;
    setCanvas({ ...c, ...local });
    try {
      const res = await updateDashboard(c.id, patch);
      announce(c.id);
      return res.data || {};
    } catch (err) {
      setCanvas((cur) => (cur && cur.id === c.id ? { ...cur, ...Object.fromEntries(Object.keys(local).map((k) => [k, c[k]])) } : cur));
      toast(detail(err) || t('agent.canvasSaveFailed'), 'error');
      return null;
    }
  }, [announce, t, toast]);

  const rename = useCallback(async (title) => {
    const clean = String(title || '').trim().slice(0, 200);
    if (!canvasRef.current || !clean || clean === canvasRef.current.title) return;
    await saveSettings({ title: clean }, { title: clean });
  }, [saveSettings]);

  /** Refresh one tile and save it. `timeRange` applies a window before the tile's own setting is saved. */
  /** What the canvas is for, shown under its title. */
  const describe = useCallback(async (description) => {
    if (!canvasRef.current) return;
    await saveSettings({ description: String(description || '').slice(0, 300) }, { description: String(description || '').slice(0, 300) });
  }, [saveSettings]);

  const refresh = useCallback(async (widgetId, { timeRange } = {}) => {
    const c = canvasRef.current;
    if (!c) return;
    mark(widgetId, 'refresh');
    try {
      const res = await refreshWidget(c.id, widgetId, timeRange || null);
      const u = res.data || {};
      patchWidget(c.id, widgetId, { data: u.data, display_type: u.display_type, chart_type: u.chart_type ?? null, refreshed_at: u.refreshed_at });
      if (c.focus) renderLive({ ...c, widgets: c.widgets.filter((w) => w.id === widgetId) }, { focus: c.focus });
    } catch (err) { toast(detail(err) || t('agent.tileRefreshFailed'), 'error'); }
    finally { mark(widgetId, null); }
  }, [mark, patchWidget, renderLive, t, toast]);

  /**
   * Refresh every live tile (no model call). Saved answers are left alone —
   * each costs model calls — and the summary, if there is one, is rewritten.
   */
  const refreshAll = useCallback(async ({ quiet = false } = {}) => {
    const c = canvasRef.current;
    if (!c || c.role === 'viewer') return;
    if (!quiet) setCanvasBusy('refreshAll');
    const live = c.widgets.filter(isLive);
    const saved = c.widgets.filter((w) => !isLive(w) && !['insight', 'summary'].includes(w.kind));
    try {
      await Promise.all(live.map(async (w) => {
        mark(w.id, 'refresh');
        try {
          const res = await refreshWidget(c.id, w.id);
          const u = res.data || {};
          patchWidget(c.id, w.id, { data: u.data, display_type: u.display_type, chart_type: u.chart_type ?? null, refreshed_at: u.refreshed_at });
        } catch { /* the tile keeps its last data */ } finally { mark(w.id, null); }
      }));
      // A quiet (automatic) refresh never calls the model: the summary is left as it is.
      if (!quiet && c.widgets.some((w) => w.kind === 'summary')) {
        try { await writeCanvasSummary(c.id); } catch { /* the old summary stays */ }
        await load(c.id, { quiet: true });
      } else if (c.focus) {
        renderLive(canvasRef.current, { focus: c.focus });
      }
      if (!quiet) toast(t('agent.canvasRefreshed', { live: live.length, saved: saved.length }));
    } finally { if (!quiet) setCanvasBusy(null); }
  }, [load, mark, patchWidget, renderLive, t, toast]);

  const setTimeRange = useCallback(async (timeRange) => {
    const c = canvasRef.current;
    if (!c || timeRange === c.time_range) return;
    const ok = await saveSettings({ time_range: timeRange }, { time_range: timeRange });
    if (ok) refreshAll();
  }, [refreshAll, saveSettings]);

  /** Filter the whole canvas to one agent, model or threat type (clicked on a chart). */
  const setFocus = useCallback(async (focus) => {
    const c = canvasRef.current;
    if (!c) return;
    if (c.role === 'viewer') {
      setCanvas({ ...c, focus });
      renderLive({ ...c, focus }, { focus });
      return;
    }
    const ok = await saveSettings(focus ? { focus } : { clear_focus: true }, { focus: focus || null });
    if (!ok) return;
    if (focus) renderLive({ ...canvasRef.current, focus }, { focus });
    else setViews({});
  }, [renderLive, saveSettings]);

  const ask = useCallback(async (widgetId, instruction, asNew = false) => {
    const c = canvasRef.current;
    if (!c) return false;
    mark(widgetId, 'ask');
    try {
      const res = await askWidget(c.id, widgetId, instruction, asNew);
      const w = res.data?.widget;
      if (!w) return false;
      setCanvas((cur) => {
        if (!cur || cur.id !== c.id) return cur;
        if (asNew) return { ...cur, widgets: withLayouts([...cur.widgets, w]) };
        return { ...cur, widgets: cur.widgets.map((x) => (x.id === widgetId ? { ...x, ...w } : x)) };
      });
      setViews((v) => { const next = { ...v }; delete next[widgetId]; return next; });
      announce(c.id);
      return true;
    } catch (err) {
      toast(detail(err) || t('agent.tileAskFailed'), 'error');
      return false;
    } finally { mark(widgetId, null); }
  }, [announce, mark, t, toast]);

  const restore = useCallback(async (widgetId, index) => {
    const c = canvasRef.current;
    if (!c) return;
    try {
      const res = await restoreWidgetVersion(c.id, widgetId, index);
      if (res.data?.widget) patchWidget(c.id, widgetId, res.data.widget);
      announce(c.id);
    } catch { toast(t('agent.canvasSaveFailed'), 'error'); }
  }, [announce, patchWidget, t, toast]);

  /** Write (or rewrite) the summary tile at the top. One model call. */
  const summarize = useCallback(async () => {
    const c = canvasRef.current;
    if (!c) return;
    setCanvasBusy('summary');
    try {
      await writeCanvasSummary(c.id);
      await load(c.id, { quiet: true });
      announce(c.id);
    } catch (err) { toast(detail(err) || t('agent.summaryFailed'), 'error'); }
    finally { setCanvasBusy(null); }
  }, [announce, load, t, toast]);

  const share = useCallback(async (mode) => {
    const ok = await saveSettings({ shared: mode }, { shared: mode });
    if (ok) toast(mode === 'org' ? t('agent.sharedWithOrg') : t('agent.sharedPrivate'));
  }, [saveSettings, t, toast]);

  return useMemo(() => ({
    canvas, status, list, busy, views, canvasBusy,
    readOnly: canvas?.role === 'viewer',
    load, loadList, findForConversation, startNew, pin, pinMany, pinText, unpin, place, arrange, rename, refresh, refreshAll,
    setTimeRange, setFocus, ask, restore, summarize, share, saveTile, addText, addTileFrom, askAbout, suggest, describe,
    isPinned: (messageId) => pinnedAnswer(canvas, messageId),
  }), [canvas, status, list, busy, views, canvasBusy, load, loadList, findForConversation, startNew, pin, pinMany, pinText,
    unpin, place, arrange, rename, refresh, refreshAll, setTimeRange, setFocus, ask, restore, summarize, share, saveTile,
    addText, addTileFrom, askAbout, suggest, describe]);
}
